import React, { useEffect, useRef } from 'react';
import SiriWave from 'siriwave';

const SiriTransitionBall = ({
    size = 120,
    isVoiceActive = false,
    voiceState = 'listening', // 'listening', 'processing', 'speaking'
    isOverlay = false,
    isDraggable = false,
    zIndex = 9999,
    amplitude = 2
}) => {
    // SIRIWAVE AMPLITUDE AMPLIFIER - 1.3x for more visible animation
    const SIRIWAVE_AMPLITUDE_MULTIPLIER = 1.3;

    // STUB: Replace dynamic amplitude with constant to test white flash
    // amplitude = 0.2;
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const waveContainerRef = useRef(null);
    const animationRef = useRef(null);
    const renderRef = useRef(null);
    const amplitudeRef = useRef(amplitude);
    const voiceStateRef = useRef(voiceState);

    // Update refs when props change - PROFESSIONAL STREAMING DATA TECHNIQUE
    useEffect(() => {
        amplitudeRef.current = amplitude;
    }, [amplitude]);

    useEffect(() => {
        voiceStateRef.current = voiceState;
    }, [voiceState]);

    useEffect(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;

        // Create WebGL canvas for shader
        const canvas = document.createElement('canvas');
        canvas.width = size * 2;  // High resolution
        canvas.height = size * 2;
        canvas.style.width = `${size}px`;
        canvas.style.height = `${size}px`;
        canvas.style.borderRadius = '50%';
        canvas.style.position = 'absolute';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.zIndex = '2';
        canvasRef.current = canvas;

        // Create SiriWave container
        const waveContainer = document.createElement('div');
        waveContainer.style.position = 'absolute';
        waveContainer.style.top = '50%';
        waveContainer.style.left = '50%';
        waveContainer.style.transform = 'translate(-50%, -50%)';
        waveContainer.style.width = `${size * 0.8}px`;
        waveContainer.style.height = `${size * 0.6}px`;
        waveContainer.style.zIndex = '3';
        waveContainerRef.current = waveContainer;

        container.appendChild(canvas);
        container.appendChild(waveContainer);

        // Initialize WebGL shader
        const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false });
        if (!gl) {
            console.error('WebGL not supported');
            return;
        }

        // Vertex shader
        const vertexShaderSource = `
            attribute vec2 a_position;
            void main() {
                gl_Position = vec4(a_position, 0.0, 1.0);
            }
        `;

        // Fragment shader with spark transition
        const fragmentShaderSource = `
            precision mediump float;
            uniform vec2 iResolution;
            uniform float iTime;
            uniform float sparkOpacity;

            #define TAU 6.28318530718

            float rand(vec2 n) {
                return fract(sin(dot(n, vec2(12.9898, 4.1414))) * 43758.5453);
            }

            float noise(vec2 p) {
                vec2 ip = floor(p);
                vec2 u = fract(p);
                u = u*u*(3.0-2.0*u);
                float res = mix(
                    mix(rand(ip),rand(ip+vec2(1.0,0.0)),u.x),
                    mix(rand(ip+vec2(0.0,1.0)),rand(ip+vec2(1.0,1.0)),u.x),u.y);
                return res*res;
            }

            float fbm(vec2 p, int octaves) {
                float s = 0.0;
                float m = 0.0;
                float a = 0.5;

                if (octaves >= 1) {
                    s += a * noise(p);
                    m += a;
                    a *= 0.5;
                    p *= 2.0;
                }

                if (octaves >= 2) {
                    s += a * noise(p);
                    m += a;
                    a *= 0.5;
                    p *= 2.0;
                }

                if (octaves >= 3) {
                    s += a * noise(p);
                    m += a;
                    a *= 0.5;
                    p *= 2.0;
                }

                if (octaves >= 4) {
                    s += a * noise(p);
                    m += a;
                    a *= 0.5;
                    p *= 2.0;
                }
                return s / m;
            }

            vec3 pal(float t, vec3 a, vec3 b, vec3 c, vec3 d) {
                return a + b * cos(TAU * (c * t + d));
            }

            float luma(vec3 color) {
              return dot(color, vec3(0.299, 0.587, 0.114));
            }

            void main() {
                float min_res = min(iResolution.x, iResolution.y);
                vec2 uv = (gl_FragCoord.xy * 2.0 - iResolution.xy) / min_res * 0.8;
                float t = iTime;

                float l = dot(uv, uv);
                if (l > 0.6) {
                    discard;
                }
                float sm = smoothstep(1.02, 0.98, l);
                float d = sm * l * l * l * 2.0;
                vec3 norm = normalize(vec3(uv.x, uv.y, .7 - d));

                float nx = fbm(uv * 2.0 + t * 0.4 + 25.69, 4);
                float ny = fbm(uv * 2.0 + t * 0.4 + 86.31, 4);
                float n = fbm(uv * 3.0 + 2.0 * vec2(nx, ny), 3);

                vec3 col = vec3(n * 0.5 + 0.25);

                float a = atan(uv.y, uv.x) / TAU + t * 0.1;
                col *= pal(a, vec3(0.3),vec3(0.5, 0.5, 0.5),vec3(1.0),vec3(0.0,0.8,0.8));

                col *= 2.0;

                vec3 cd = abs(col);
                vec3 c = col * d;
                c += (c * 0.5 + vec3(1.0) - luma(c)) * vec3(max(0.0, pow(dot(norm, vec3(0.0, 0.0, -1.0)), 5.0) * 3.0));

                float g = 1.5 * smoothstep(0.6, 1.0, fbm(norm.xy * 3.0 / (1.0 + norm.z), 2)) * d;
                c += g;

                col = c + col * pow((1.0 - smoothstep(1.0, 0.98, l) - pow(max(0.0, length(uv) - 1.0), 0.2)) * 2.0, 4.0);

                // SPARK EFFECTS - controlled by sparkOpacity
                if (sparkOpacity > 0.0) {
                    float f = fbm(normalize(uv) * 2.0 + t * 0.5, 2) + 0.1;
                    uv *= f + 0.1;
                    uv *= 0.5;
                    l = dot(uv, uv);
                    vec3 ins = normalize(cd) + 0.1;
                    float ind = 0.2 + pow(smoothstep(0.0, 1.5, sqrt(l)) * 48.0, 0.25);
                    ind *= ind * ind * ind;
                    ind = 1.0 / ind;
                    ins *= ind;
                    col += ins * ins * sm * smoothstep(0.7, 1.0, ind) * sparkOpacity * 0.4; // CONFIRMED: NOT WHITE FLASH SOURCE - PROPERLY CALIBRATED
                }

                col += abs(norm) * (1.0 - d) * sm * 0.25;

                float energyStrength = length(col);
                float alpha = mix(0.5, 1.0, energyStrength);
                gl_FragColor = vec4(col, alpha);
            }
        `;

        function createShader(gl, type, source) {
            const shader = gl.createShader(type);
            gl.shaderSource(shader, source);
            gl.compileShader(shader);
            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                console.error('Error compiling shader:', gl.getShaderInfoLog(shader));
                gl.deleteShader(shader);
                return null;
            }
            return shader;
        }

        const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
        const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        const positionBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1
        ]), gl.STATIC_DRAW);

        const positionLocation = gl.getAttribLocation(program, 'a_position');
        const resolutionLocation = gl.getUniformLocation(program, 'iResolution');
        const timeLocation = gl.getUniformLocation(program, 'iTime');
        const sparkOpacityLocation = gl.getUniformLocation(program, 'sparkOpacity');

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

        // Control variables
        let siriWaveOpacity = 1.0;
        let sparkOpacity = 0.0;
        let ballOpacity = 1.0;

        // Initialize SiriWave
        let siriWave = null;
        if (SiriWave) {
            siriWave = new SiriWave({
                container: waveContainer,
                width: size * 0.8,
                height: size * 0.6,
                style: "ios9",
                amplitude: amplitude,
                speed: 0.15,
                frequency: 2,
                color: "#ffffff",
                cover: false,
                autostart: true
            });
        }

        function render() {
            gl.viewport(0, 0, canvas.width, canvas.height);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);

            gl.useProgram(program);
            gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
            gl.uniform1f(timeLocation, performance.now() * 0.001);
            gl.uniform1f(sparkOpacityLocation, sparkOpacity);

            gl.enableVertexAttribArray(positionLocation);
            gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
            gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
            gl.drawArrays(gl.TRIANGLES, 0, 6);

            renderRef.current = requestAnimationFrame(render);
        }

        function updateTransition() {
            // PROFESSIONAL TECHNIQUE: Read current voiceState from ref
            const currentVoiceState = voiceStateRef.current;
            // eslint-disable-next-line no-unused-vars
            const currentAmplitude = amplitudeRef.current; // Reserved for future amplitude-based animations
            // console.log("🎙️ VOICE STATE:", currentVoiceState, "AMPLITUDE:", currentAmplitude);
            if (currentVoiceState === 'listening') {
                // LISTENING STATE - SHOW SPARKS (swapped animation)

                // GROUP 1: SIRIWAVE OFF
                siriWaveOpacity = 0.0;
                waveContainer.style.opacity = siriWaveOpacity.toString();

                // GROUP 2: SPARKS ON - AMPLITUDE CONTROLLED
                const currentAmplitude = amplitudeRef.current;
                sparkOpacity = currentAmplitude;

                // GROUP 3: BALL ALWAYS ON
                ballOpacity = 1.0;
                canvas.style.opacity = ballOpacity.toString();
            } else if (currentVoiceState === 'processing') {
                // PROCESSING STATE - SHOW SIRIWAVE (swapped animation)
                if (siriWave) {
                    const currentAmplitude = amplitudeRef.current;
                    const scaledAmplitude = Math.max(1, currentAmplitude * 6 * SIRIWAVE_AMPLITUDE_MULTIPLIER);
                    if (siriWave.opt) siriWave.opt.amplitude = scaledAmplitude;
                    if (siriWave.amplitude !== undefined) siriWave.amplitude = scaledAmplitude;
                }

                // GROUP 1: SIRIWAVE ON
                siriWaveOpacity = 1.0;
                waveContainer.style.opacity = siriWaveOpacity.toString();

                // GROUP 2: SPARKS OFF
                sparkOpacity = 0.0;

                // GROUP 3: BALL ALWAYS ON
                ballOpacity = 1.0;
                canvas.style.opacity = ballOpacity.toString();
            } else if (currentVoiceState === 'speaking') {
                // AI IS TALKING - SHOW SPARKS (swapped animation)

                // GROUP 1: SIRIWAVE OFF
                siriWaveOpacity = 0.0;
                waveContainer.style.opacity = siriWaveOpacity.toString();

                // GROUP 2: SPARKS ON - AMPLITUDE CONTROLLED
                const currentAmplitude = amplitudeRef.current;
                sparkOpacity = currentAmplitude;

                // GROUP 3: BALL ALWAYS ON
                ballOpacity = 1.0;
                canvas.style.opacity = ballOpacity.toString();
            }
            animationRef.current = requestAnimationFrame(updateTransition);
        }

        render();
        updateTransition();

        // Make draggable if enabled
        if (isDraggable) {
            let isDragging = false;
            let startX, startY;

            const handleMouseDown = (e) => {
                isDragging = true;
                startX = e.clientX - container.offsetLeft;
                startY = e.clientY - container.offsetTop;
                container.style.cursor = 'grabbing';
            };

            const handleMouseMove = (e) => {
                if (!isDragging) return;
                const newX = e.clientX - startX;
                const newY = e.clientY - startY;
                container.style.left = `${newX}px`;
                container.style.top = `${newY}px`;
                container.style.right = 'auto';
                container.style.bottom = 'auto';
            };

            const handleMouseUp = () => {
                isDragging = false;
                container.style.cursor = 'grab';
            };

            container.addEventListener('mousedown', handleMouseDown);
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                container.removeEventListener('mousedown', handleMouseDown);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (renderRef.current) {
                cancelAnimationFrame(renderRef.current);
            }
            if (siriWave) {
                siriWave.stop();
            }
            // Clean up WebGL context
            if (gl) {
                gl.getExtension('WEBGL_lose_context')?.loseContext();
            }
            // Clean up DOM elements
            if (container) {
                container.innerHTML = '';
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [size, isDraggable]); // PRO TECHNIQUE: amplitude handled via ref to avoid re-render loops

    const containerClasses = [
        'siri-transition-ball',
        isOverlay ? 'overlay' : 'normal',
        isDraggable ? 'draggable' : ''
    ].filter(Boolean).join(' ');

    const containerStyle = {
        width: isOverlay ? '100vw' : `${size}px`,
        height: isOverlay ? '100vh' : `${size}px`
    };

    if (isOverlay) {
        return (
            <div className={containerClasses} style={containerStyle}>
                <div
                    ref={containerRef}
                    className="siri-ball-inner"
                    style={{
                        width: `${size}px`,
                        height: `${size}px`
                    }}
                />
            </div>
        );
    }

    return <div ref={containerRef} className={containerClasses} style={containerStyle} />;
};

export default SiriTransitionBall;